import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { AppLinearGradient as LinearGradient } from '../../components/ui/AppLinearGradient';
import { appTheme, useThemeColors } from '../../shared/theme';
import { hexToRgba } from '../../shared/theme/colorUtils';
import type { ThemeColors } from '../../shared/theme/store';
import { useTranslation, formatNumber, layoutLtrStyle } from '../../shared/i18n';
import { useRtl } from '../../shared/i18n/useRtl';
import { androidScaleFont, androidScaleSize } from '../../shared/theme/sharedStyles';
import { getFormattedPlanRange } from '../../data/domains/calendar';

export interface CalendarHeaderPlanCardProps {
  plan_name: string;
  start_date: string;
  end_date: string;
  days_left: number;
  /** Backend-paused days from today; when > 0 caption becomes “remaining, y paused”. */
  paused_days_left?: number;
  progress_percentage: number;
}

function createStyles(colors: ThemeColors, text_align: 'left' | 'right') {
  const sf = androidScaleFont;
  const ss = androidScaleSize;
  return StyleSheet.create({
    panel: {
      borderRadius: ss(14),
      paddingHorizontal: ss(12),
      paddingVertical: ss(4),
      paddingTop: ss(8),
      backgroundColor: colors.surfaceElevated,
    },
    panelRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: ss(6),
      marginBottom: ss(4),
    },
    planName: {
      fontSize: sf(16),
      lineHeight: sf(16),
      fontWeight: '500',
      color: colors.text,
      letterSpacing: -0.15,
      textAlign: text_align,
      marginBottom: ss(4),
    },
    daysRow: {
      flexDirection: 'row',
      alignItems: 'baseline',
      gap: ss(4),
      marginBottom: ss(2),
    },
    daysValue: {
      fontSize: sf(16),
      lineHeight: sf(16),
      fontWeight: '500',
      color: colors.primary,
      fontVariant: ['tabular-nums'] as any,
    },
    daysCaption: {
      flex: 1,
      minWidth: 0,
      fontSize: sf(12),
      lineHeight: sf(12),
      fontWeight: '400',
      color: colors.textSecondary,
      textAlign: text_align,
    },
    progressTrack: {
      ...layoutLtrStyle,
      height: ss(5),
      borderRadius: 100,
      backgroundColor: (hexToRgba(colors.text, 0.08) as string) || colors.background,
      overflow: 'hidden',
      marginTop: ss(4),
      marginBottom: ss(4),
    },
    progressFill: {
      height: '100%',
      borderRadius: 100,
      minWidth: 2,
    },
    dateRange: {
      fontSize: sf(10),
      lineHeight: sf(10),
      fontWeight: '200',
      color: colors.textSecondary,
      textAlign: text_align,
      letterSpacing: 0.5,
    },
  });
}

export const CalendarHeaderPlanCard = React.memo(function CalendarHeaderPlanCard({
  plan_name,
  start_date,
  end_date,
  days_left,
  paused_days_left = 0,
  progress_percentage,
}: CalendarHeaderPlanCardProps) {
  const { t, locale } = useTranslation();
  const rtl = useRtl();
  const themeColors = useThemeColors();
  const palette = themeColors ?? appTheme.colors;
  const writing = rtl.writingDirection;
  const text_align = rtl.textAlign;

  const styles = useMemo(() => createStyles(palette, text_align), [palette, text_align]);

  const daysLeftDisplay = Math.max(0, days_left);
  const pausedDaysDisplay = Math.max(0, paused_days_left);
  const dateRangeLabel = getFormattedPlanRange(start_date, end_date, locale);
  const progressWidth = `${Math.min(100, Math.max(0, progress_percentage))}%` as `${number}%`;
  const days_caption =
    pausedDaysDisplay > 0
      ? t('calendar.deliveryDaysHeroCaptionPaused', { paused: pausedDaysDisplay })
      : t('calendar.deliveryDaysHeroCaption');

  return (
    <View style={styles.panel} accessibilityRole="summary">
      <View style={styles.panelRow} accessibilityRole="summary">
      <Text style={[styles.planName, { writingDirection: writing }]} numberOfLines={1} selectable>
        {plan_name}
      </Text>
      <Text style={[styles.dateRange, { writingDirection: writing }]} numberOfLines={1} selectable>
        {dateRangeLabel}
      </Text>
      </View>
      <View style={styles.daysRow}>
        <Text style={[styles.daysValue, { writingDirection: writing }]} accessibilityRole="text">
          {formatNumber(daysLeftDisplay)}
        </Text>
        <Text style={[styles.daysCaption, { writingDirection: writing }]} numberOfLines={2}>
          {days_caption}
        </Text>
      </View>
      <View style={styles.progressTrack} accessibilityRole="progressbar">
        <LinearGradient
          colors={[palette.primaryLight ?? palette.primary, palette.primary]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          style={[styles.progressFill, { width: progressWidth }]}
        />
      </View>

    </View>
  );
});
